import { useState, useEffect, useCallback } from "react";
import { AlertTriangle, Bell, Shield, Activity, Clock, TrendingUp, X, Settings } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { BentoGrid, BentoGridItem } from "@/components/ui/bento-grid";
import { useToast } from "@/components/ui/use-toast";

interface RateLimitWarning {
  id: string;
  type: 'dns' | 'api';
  scope: 'ip' | 'organization';
  window: 'second' | 'minute' | 'hour' | 'day';
  current: number;
  limit: number;
  percentage: number;
  severity: 'info' | 'warning' | 'critical';
  timestamp: number;
  acknowledged: boolean;
}

interface WarningSettings {
  enabled: boolean;
  thresholds: {
    info: number;    // Show info at this percentage
    warning: number; // Show warning at this percentage
    critical: number; // Show critical at this percentage
  };
  notifications: {
    browser: boolean;
    toast: boolean;
    persistent: boolean;
  };
  autoAcknowledge: boolean;
  autoAcknowledgeDelay: number; // seconds
}

interface RateLimitWarningsProps {
  dnsServerUrl?: string;
  refreshInterval?: number;
  initialSettings?: Partial<WarningSettings>;
}

export function RateLimitWarnings({ 
  dnsServerUrl = import.meta.env.VITE_DNS_SERVER_URL ?? "http://localhost:3001",
  refreshInterval = 5000,
  initialSettings = {}
}: RateLimitWarningsProps) {
  const { toast } = useToast();
  
  const [warnings, setWarnings] = useState<RateLimitWarning[]>([]);
  const [settings, setSettings] = useState<WarningSettings>({
    enabled: true,
    thresholds: {
      info: 60,
      warning: 80,
      critical: 95
    },
    notifications: {
      browser: true,
      toast: true,
      persistent: false
    },
    autoAcknowledge: false,
    autoAcknowledgeDelay: 30,
    ...initialSettings
  });
  const [showSettings, setShowSettings] = useState(false);
  const [rateLimitData, setRateLimitData] = useState<any>(null);

  // Fetch rate limit data
  const fetchRateLimitData = useCallback(async () => {
    if (!settings.enabled) return;

    try {
      const response = await fetch(`${dnsServerUrl}/rate-limits`);
      if (response.ok) {
        const data = await response.json();
        setRateLimitData(data);
        
        if (data.enabled && data.usage && data.limits) {
          updateWarnings(data);
        }
      }
    } catch (error) {
      console.error('Failed to fetch rate limit data:', error);
    }
  }, [dnsServerUrl, settings.enabled]);

  // Update warnings based on current usage
  const updateWarnings = useCallback((data: any) => {
    const newWarnings: RateLimitWarning[] = [];
    const now = Date.now();

    // Check IP limits
    if (data.usage.ip) {
      ['dns', 'api'].forEach((type) => {
        if (data.usage.ip[type] && data.limits[type]) {
          Object.entries(data.usage.ip[type]).forEach(([window, usage]: [string, any]) => {
            const limitKey = `per${window.charAt(0).toUpperCase() + window.slice(1)}` as keyof typeof data.limits[typeof type];
            const limit = data.limits[type][limitKey];
            
            if (limit && usage.count > 0) {
              const percentage = (usage.count / limit) * 100;
              let severity: 'info' | 'warning' | 'critical' = 'info';
              
              if (percentage >= settings.thresholds.critical) severity = 'critical';
              else if (percentage >= settings.thresholds.warning) severity = 'warning';
              else if (percentage >= settings.thresholds.info) severity = 'info';
              
              if (percentage >= settings.thresholds.info) {
                const warningId = `ip-${type}-${window}`;
                
                newWarnings.push({
                  id: warningId,
                  type: type as 'dns' | 'api',
                  scope: 'ip',
                  window: window as 'second' | 'minute' | 'hour' | 'day',
                  current: usage.count,
                  limit,
                  percentage,
                  severity,
                  timestamp: now,
                  acknowledged: false
                });
              }
            }
          });
        }
      });
    }

    // Check organization limits
    if (data.usage.organization) {
      ['dns', 'api'].forEach((type) => {
        if (data.usage.organization[type] && data.limits.organization) {
          Object.entries(data.usage.organization[type]).forEach(([window, usage]: [string, any]) => {
            const limitKey = `${type}Per${window.charAt(0).toUpperCase() + window.slice(1)}` as keyof typeof data.limits.organization;
            const limit = data.limits.organization[limitKey];
            
            if (limit && usage.count > 0) {
              const percentage = (usage.count / limit) * 100;
              let severity: 'info' | 'warning' | 'critical' = 'info';
              
              if (percentage >= settings.thresholds.critical) severity = 'critical';
              else if (percentage >= settings.thresholds.warning) severity = 'warning';
              else if (percentage >= settings.thresholds.info) severity = 'info';
              
              if (percentage >= settings.thresholds.info) {
                const warningId = `org-${type}-${window}`;
                
                newWarnings.push({
                  id: warningId,
                  type: type as 'dns' | 'api',
                  scope: 'organization',
                  window: window as 'second' | 'minute' | 'hour' | 'day',
                  current: usage.count,
                  limit,
                  percentage,
                  severity,
                  timestamp: now,
                  acknowledged: false
                });
              }
            }
          });
        }
      });
    }

    // Merge with existing warnings, preserving acknowledged status
    setWarnings(prevWarnings => {
      const mergedWarnings = newWarnings.map(newWarning => {
        const existing = prevWarnings.find(w => w.id === newWarning.id);
        return existing ? { ...newWarning, acknowledged: existing.acknowledged } : newWarning;
      });

      // Show notifications for new critical warnings
      const newCriticalWarnings = mergedWarnings.filter(w => 
        w.severity === 'critical' && 
        !w.acknowledged && 
        !prevWarnings.some(prev => prev.id === w.id && prev.severity === 'critical')
      );

      newCriticalWarnings.forEach(warning => showNotification(warning));

      return mergedWarnings;
    });
  }, [settings.thresholds]);

  // Show notification
  const showNotification = useCallback((warning: RateLimitWarning) => {
    const title = `Rate Limit ${warning.severity.toUpperCase()}`;
    const description = `${warning.type.toUpperCase()} ${warning.scope} usage: ${warning.current}/${warning.limit} (${Math.round(warning.percentage)}%) per ${warning.window}`;

    // Show toast notification
    if (settings.notifications.toast) {
      toast({
        title,
        description,
        variant: warning.severity === 'critical' ? 'destructive' : 'default',
      });
    }

    // Show browser notification
    if (settings.notifications.browser && 'Notification' in window) {
      if (Notification.permission === 'granted') {
        new Notification(title, {
          body: description,
          icon: '/favicon.ico'
        });
      } else if (Notification.permission !== 'denied') {
        Notification.requestPermission().then(permission => {
          if (permission === 'granted') {
            new Notification(title, {
              body: description,
              icon: '/favicon.ico'
            });
          }
        });
      }
    }
  }, [settings.notifications, toast]);

  // Auto-acknowledge warnings
  useEffect(() => {
    if (!settings.autoAcknowledge) return;

    const interval = setInterval(() => {
      const now = Date.now();
      setWarnings(prev => prev.map(warning => ({
        ...warning,
        acknowledged: warning.acknowledged || 
          (now - warning.timestamp > settings.autoAcknowledgeDelay * 1000)
      })));
    }, 1000);

    return () => clearInterval(interval);
  }, [settings.autoAcknowledge, settings.autoAcknowledgeDelay]);

  // Fetch data periodically
  useEffect(() => {
    fetchRateLimitData();
    const interval = setInterval(fetchRateLimitData, refreshInterval);
    return () => clearInterval(interval);
  }, [fetchRateLimitData, refreshInterval]);

  // Request notification permission on mount
  useEffect(() => {
    if (settings.notifications.browser && 'Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, [settings.notifications.browser]);

  const acknowledgeWarning = (id: string) => {
    setWarnings(prev => prev.map(w => w.id === id ? { ...w, acknowledged: true } : w));
  };

  const acknowledgeAll = () => {
    setWarnings(prev => prev.map(w => ({ ...w, acknowledged: true })));
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical': return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case 'warning': return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'info': return <Bell className="h-4 w-4 text-blue-500" />;
      default: return <Bell className="h-4 w-4" />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'border-red-500/50 bg-red-500/10';
      case 'warning': return 'border-yellow-500/50 bg-yellow-500/10';
      case 'info': return 'border-blue-500/50 bg-blue-500/10';
      default: return 'border-primary/20 bg-background/80';
    }
  };

  const activeWarnings = warnings.filter(w => !w.acknowledged);
  const acknowledgedWarnings = warnings.filter(w => w.acknowledged);

  return (
    <div className="space-y-6">
      <Card className="bg-background/80 backdrop-blur-xl border border-primary/20">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Shield className="h-5 w-5 text-primary" />
              <CardTitle className="text-foreground">Rate Limit Warnings</CardTitle>
              {activeWarnings.length > 0 && (
                <Badge variant="outline" className="text-red-500 border-red-500/30">
                  {activeWarnings.length} active
                </Badge>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <Button 
                onClick={() => setShowSettings(!showSettings)} 
                variant="outline" 
                size="sm"
              >
                <Settings className="h-3 w-3 mr-1" />
                Settings
              </Button>
              {activeWarnings.length > 0 && (
                <Button 
                  onClick={acknowledgeAll} 
                  variant="outline" 
                  size="sm"
                >
                  Acknowledge All
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {showSettings && (
            <div className="mb-6 p-4 border border-primary/20 rounded-lg bg-background/50">
              <h4 className="font-semibold text-foreground mb-4">Warning Settings</h4>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Enable warnings</Label>
                  <Switch 
                    checked={settings.enabled}
                    onCheckedChange={(enabled) => setSettings(prev => ({ ...prev, enabled }))}
                  />
                </div>

                <div className="space-y-3">
                  <Label>Warning thresholds (%)</Label>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs text-blue-500">Info: {settings.thresholds.info}%</Label>
                      <Slider
                        value={[settings.thresholds.info]}
                        onValueChange={([value]) => 
                          setSettings(prev => ({ 
                            ...prev, 
                            thresholds: { ...prev.thresholds, info: value }
                          }))
                        }
                        max={100}
                        step={5}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-yellow-500">Warning: {settings.thresholds.warning}%</Label>
                      <Slider
                        value={[settings.thresholds.warning]}
                        onValueChange={([value]) => 
                          setSettings(prev => ({ 
                            ...prev, 
                            thresholds: { ...prev.thresholds, warning: value }
                          }))
                        }
                        max={100}
                        step={5}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-red-500">Critical: {settings.thresholds.critical}%</Label>
                      <Slider
                        value={[settings.thresholds.critical]}
                        onValueChange={([value]) => 
                          setSettings(prev => ({ 
                            ...prev, 
                            thresholds: { ...prev.thresholds, critical: value }
                          }))
                        }
                        max={100}
                        step={5}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Notification methods</Label>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Browser notifications</span>
                      <Switch 
                        checked={settings.notifications.browser}
                        onCheckedChange={(browser) => 
                          setSettings(prev => ({ 
                            ...prev, 
                            notifications: { ...prev.notifications, browser }
                          }))
                        }
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Toast notifications</span>
                      <Switch 
                        checked={settings.notifications.toast}
                        onCheckedChange={(toast) => 
                          setSettings(prev => ({ 
                            ...prev, 
                            notifications: { ...prev.notifications, toast }
                          }))
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Auto-acknowledge after {settings.autoAcknowledgeDelay}s</Label>
                    <Switch 
                      checked={settings.autoAcknowledge}
                      onCheckedChange={(autoAcknowledge) => 
                        setSettings(prev => ({ ...prev, autoAcknowledge }))
                      }
                    />
                  </div>
                  {settings.autoAcknowledge && (
                    <Slider
                      value={[settings.autoAcknowledgeDelay]}
                      onValueChange={([value]) => 
                        setSettings(prev => ({ ...prev, autoAcknowledgeDelay: value }))
                      }
                      min={5}
                      max={300}
                      step={5}
                      className="mt-2"
                    />
                  )}
                </div>
              </div>
            </div>
          )}

          {!settings.enabled ? (
            <div className="text-center py-8 text-muted-foreground">
              Rate limit warnings are disabled
            </div>
          ) : activeWarnings.length === 0 ? (
            <div className="text-center py-8">
              <Shield className="h-8 w-8 text-green-500 mx-auto mb-2" />
              <div className="text-green-600 font-medium">All systems normal</div>
              <div className="text-muted-foreground text-sm">No rate limit warnings</div>
            </div>
          ) : (
            <div className="space-y-4">
              <h4 className="font-semibold text-foreground">Active Warnings</h4>
              <div className="space-y-3">
                {activeWarnings.map((warning) => (
                  <Alert key={warning.id} className={getSeverityColor(warning.severity)}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3">
                        {getSeverityIcon(warning.severity)}
                        <div className="flex-1">
                          <AlertTitle className="text-sm font-medium">
                            {warning.type.toUpperCase()} Rate Limit - {warning.scope} ({warning.window})
                          </AlertTitle>
                          <AlertDescription className="text-xs mt-1">
                            Usage: {warning.current}/{warning.limit} requests ({Math.round(warning.percentage)}%)
                          </AlertDescription>
                          <Progress value={warning.percentage} className="h-2 mt-2" />
                        </div>
                      </div>
                      <Button 
                        onClick={() => acknowledgeWarning(warning.id)}
                        variant="ghost" 
                        size="sm"
                        className="h-6 w-6 p-0 hover:bg-background/50"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  </Alert>
                ))}
              </div>
            </div>
          )}

          {acknowledgedWarnings.length > 0 && (
            <div className="mt-6 space-y-4">
              <h4 className="font-semibold text-muted-foreground text-sm">Recently Acknowledged</h4>
              <div className="space-y-2">
                {acknowledgedWarnings.slice(-3).map((warning) => (
                  <div key={warning.id} className="flex items-center space-x-3 text-xs text-muted-foreground p-2 bg-background/30 rounded">
                    {getSeverityIcon(warning.severity)}
                    <span>
                      {warning.type.toUpperCase()} {warning.scope} ({warning.window}): 
                      {warning.current}/{warning.limit} ({Math.round(warning.percentage)}%)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}